import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle, FiArchive, FiTrash2 } from 'react-icons/fi';
import ModalPasos from '../../../../components/ui/ModalPasos';
import { CampoSelect, CampoTexto } from './CamposFormulario';
import { esStaff } from '../../../../constants/usuarios';
import BASE_URL from '../../../../config/config';
import { cabecerasAuth } from '../../../../queries/cliente';

/**
 * Baja de una cuenta, en dos pasos: qué se hace con ella y confirmación.
 *
 * El primer paso carga el impacto real (cuántos tickets, notificaciones,
 * archivos…) para que nadie elija "eliminar" a ciegas, y obliga a decir quién
 * recoge los tickets que la cuenta tenga sin cerrar.
 *
 * Para confirmar hay que escribir el nombre de usuario: es la barrera estándar
 * de las acciones que no se pueden deshacer.
 */
const ModalEliminarUsuario = ({ abierto, usuario, rol, tecnicos = [], onCerrar, onHecho }) => {
  const { t } = useTranslation();
  const [modo, setModo] = useState('archivar');
  const [reasignarA, setReasignarA] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [impacto, setImpacto] = useState(null);
  const [procesando, setProcesando] = useState(false);

  const destinos = useMemo(
    () => tecnicos.filter((tecnico) => tecnico.usuario !== usuario && tecnico.estado !== 'archivado'),
    [tecnicos, usuario],
  );

  useEffect(() => {
    if (!abierto) return;
    setModo('archivar');
    setReasignarA('');
    setConfirmacion('');
    setImpacto(null);
    axios.get(`${BASE_URL}/usuario/${usuario}/impacto-baja`, cabecerasAuth())
      .then((r) => setImpacto(r.data.impacto))
      .catch(() => setImpacto(null));
  }, [abierto, usuario]);

  const activos = impacto?.tickets_asignados_activos || 0;
  const hayQueReasignar = activos > 0;

  const lanzar = async () => {
    setProcesando(true);
    try {
      const { data } = await axios.delete(`${BASE_URL}/usuario/${usuario}`, {
        ...cabecerasAuth(),
        data: { modo, reasignarA: hayQueReasignar ? reasignarA : undefined },
      });
      onHecho(data);
    } catch (error) {
      // El mensaje del servidor es el que explica por qué no se pudo
      throw new Error(error.response?.data?.message || t('bajaUsuario.error'));
    } finally {
      setProcesando(false);
    }
  };

  const filaImpacto = (clave, valor) => (
    <div key={clave}><dt>{t(`bajaUsuario.impacto.${clave}`)}</dt><dd>{valor}</dd></div>
  );

  const pasos = [
    {
      clave: 'modo',
      titulo: t('bajaUsuario.pasoQueHacer'),
      descripcion: t('bajaUsuario.pasoQueHacerDesc', { usuario }),
      validar: () => {
        if (hayQueReasignar && !reasignarA) return t('bajaUsuario.faltaReasignar', { count: activos });
        return null;
      },
      contenido: (
        <div className="fcu-campos">
          <div className="bu-opciones" role="radiogroup" aria-label={t('bajaUsuario.pasoQueHacer')}>
            <button
              type="button"
              role="radio"
              aria-checked={modo === 'archivar'}
              className={`bu-opcion ${modo === 'archivar' ? 'elegida' : ''}`}
              onClick={() => setModo('archivar')}
            >
              <FiArchive />
              <span>
                <strong>{t('bajaUsuario.archivar')}</strong>
                <small>{t('bajaUsuario.archivarDesc')}</small>
              </span>
            </button>

            <button
              type="button"
              role="radio"
              aria-checked={modo === 'eliminar'}
              className={`bu-opcion peligrosa ${modo === 'eliminar' ? 'elegida' : ''}`}
              onClick={() => setModo('eliminar')}
            >
              <FiTrash2 />
              <span>
                <strong>{t('bajaUsuario.eliminar')}</strong>
                <small>{t('bajaUsuario.eliminarDesc')}</small>
              </span>
            </button>
          </div>

          {/* El trabajo vivo no puede quedarse sin responsable */}
          {hayQueReasignar && (
            <CampoSelect
              id="bu-reasignar"
              label={t('bajaUsuario.reasignarA')}
              hint={t('bajaUsuario.reasignarHint', { count: activos })}
              value={reasignarA}
              onChange={(e) => setReasignarA(e.target.value)}
            >
              <option value="">{t('comun.seleccionarPlaceholder')}</option>
              {destinos.map((tecnico) => (
                <option key={tecnico.usuario} value={tecnico.usuario}>
                  {tecnico.nombre} {tecnico.apellidos} (@{tecnico.usuario})
                </option>
              ))}
            </CampoSelect>
          )}

          {!esStaff(rol) && activos === 0 && (
            <p className="bu-nota">{t('bajaUsuario.notaCliente')}</p>
          )}
        </div>
      ),
    },
    {
      clave: 'confirmar',
      titulo: t('bajaUsuario.pasoConfirmar'),
      descripcion: modo === 'eliminar'
        ? t('bajaUsuario.confirmarEliminarDesc')
        : t('bajaUsuario.confirmarArchivarDesc'),
      validar: () => (confirmacion.trim() === usuario ? null : t('bajaUsuario.confirmacionNoCoincide', { usuario })),
      contenido: (
        <div className="fcu-campos">
          {impacto && (
            <div className="fcu-resumen">
              <h5>{t('bajaUsuario.queSeVe')}</h5>
              <dl>
                {filaImpacto('tickets_creados', impacto.tickets_creados)}
                {filaImpacto('tickets_asignados_activos', impacto.tickets_asignados_activos)}
                {filaImpacto('tickets_asignados_cerrados', impacto.tickets_asignados_cerrados)}
                {filaImpacto('notificaciones', impacto.notificaciones)}
                {filaImpacto('archivos_drive', impacto.archivos_drive)}
                {filaImpacto('servicios', impacto.servicios)}
                {filaImpacto('relaciones', impacto.relaciones)}
                {rol === 'admin' && filaImpacto('tokens_mcp', impacto.tokens_mcp)}
              </dl>
            </div>
          )}

          <div className={`bu-aviso ${modo === 'eliminar' ? 'peligrosa' : ''}`}>
            <FiAlertTriangle />
            <p>{modo === 'eliminar' ? t('bajaUsuario.avisoEliminar') : t('bajaUsuario.avisoArchivar')}</p>
          </div>

          <CampoTexto
            id="bu-confirmacion"
            label={t('bajaUsuario.escribeUsuario', { usuario })}
            value={confirmacion}
            onChange={(e) => setConfirmacion(e.target.value)}
            placeholder={usuario}
          />
        </div>
      ),
    },
  ];

  return (
    <ModalPasos
      abierto={abierto}
      titulo={t('bajaUsuario.titulo', { usuario })}
      pasos={pasos}
      procesando={procesando}
      textoFinalizar={modo === 'eliminar' ? t('bajaUsuario.eliminar') : t('bajaUsuario.archivar')}
      onFinalizar={lanzar}
      onCerrar={onCerrar}
    />
  );
};

export default ModalEliminarUsuario;
